/** Browser SVG adapter. All projection, shape paths, and sizing are shared
 * with the mobile renderer; input is handled by the editor surface. */

import React from "react";
import {
  arrowPathD,
  ellipsePx,
  freehandPathD,
  strokePxFor,
  textPx,
  type FitRect,
} from "../../shared/photo-annotations/annotationGeometry";
import {
  TEXT_HALO_MIN_PX,
  TEXT_HALO_RATIO,
  type AnnotationDoc,
  type AnnotationItem,
} from "../../shared/photo-annotations/annotationSchema";

/**
 * Annotation ink and the selection handle. Deliberately fixed: these are drawn
 * *on the photo*, and the photo does not change with the app's profile. Same
 * rule as map markers and the roof-line colour on the trace canvas.
 */
const SELECTION_COLOR = "#FFD700";

interface AnnotationLayerProps {
  doc: AnnotationDoc;
  /** Where the photo sits, in the coordinate space of this layer's parent. */
  rect: FitRect;
  /** Drawn with a gold halo so it reads over any photo. */
  selectedId?: string | null;
  /** Overall canvas size. Defaults to covering the rect plus its offset. */
  width?: number;
  height?: number;
  /** Skip pointer handling entirely — the parent owns gestures. */
  pointerEvents?: "none" | "auto";
}

function renderItem(
  item: AnnotationItem,
  rect: FitRect,
  isSelected: boolean,
): React.ReactNode {
  const selectionWidth =
    strokePxFor("width" in item ? item.width : 0.009, rect) + 4;

  switch (item.kind) {
    case "arrow": {
      const d = arrowPathD(item, rect);
      const stroke = strokePxFor(item.width, rect);
      return (
        <React.Fragment key={item.id}>
          {isSelected ? (
            <path
              d={d}
              stroke={SELECTION_COLOR}
              strokeWidth={selectionWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          ) : null}
          <path
            d={d}
            stroke={item.color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </React.Fragment>
      );
    }

    case "ellipse": {
      const e = ellipsePx(item, rect);
      const stroke = strokePxFor(item.width, rect);
      return (
        <React.Fragment key={item.id}>
          {isSelected ? (
            <ellipse
              cx={e.cx}
              cy={e.cy}
              rx={e.rx}
              ry={e.ry}
              stroke={SELECTION_COLOR}
              strokeWidth={selectionWidth}
              fill="none"
            />
          ) : null}
          <ellipse
            cx={e.cx}
            cy={e.cy}
            rx={e.rx}
            ry={e.ry}
            stroke={item.color}
            strokeWidth={stroke}
            fill="none"
          />
        </React.Fragment>
      );
    }

    case "freehand": {
      const d = freehandPathD(item, rect);
      const stroke = strokePxFor(item.width, rect);
      return (
        <React.Fragment key={item.id}>
          {isSelected ? (
            <path
              d={d}
              stroke={SELECTION_COLOR}
              strokeWidth={selectionWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          ) : null}
          <path
            d={d}
            stroke={item.color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </React.Fragment>
      );
    }

    case "text": {
      const t = textPx(item, rect);
      const halo = item.halo
        ? Math.max(TEXT_HALO_MIN_PX, Math.round(TEXT_HALO_RATIO * t.fontPx))
        : 0;
      return (
        <React.Fragment key={item.id}>
          {isSelected ? (
            <text
              x={t.x}
              y={t.y}
              fill="none"
              stroke={SELECTION_COLOR}
              strokeWidth={halo + 4}
              {...({ fontSize: t.fontPx, fontWeight: "bold" } as object)}
            >
              {item.text}
            </text>
          ) : null}
          {halo > 0 ? (
            // Painted first so the fill below sits on top of its own outline.
            <text
              x={t.x}
              y={t.y}
              // Halo behind the label, drawn on the photo — see the note above.
              fill="#000000"
              stroke="#000000"
              strokeWidth={halo}
              {...({
                fontSize: t.fontPx,
                fontWeight: "bold",
                strokeLinejoin: "round",
              } as object)}
            >
              {item.text}
            </text>
          ) : null}
          <text
            x={t.x}
            y={t.y}
            fill={item.color}
            {...({ fontSize: t.fontPx, fontWeight: "bold" } as object)}
          >
            {item.text}
          </text>
        </React.Fragment>
      );
    }
  }
}

export const AnnotationLayer: React.FC<AnnotationLayerProps> = React.memo(
  ({ doc, rect, selectedId = null, width, height, pointerEvents = "none" }) => {
    if (!doc.items.length) return null;

    return (
      <svg
        // A real prop, not `style.pointerEvents` — react-native-svg does not
        // forward that through its style handling, so the overlay would keep
        // swallowing touches meant for the canvas underneath.
        aria-hidden="true"
        pointerEvents={pointerEvents}
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          fontFamily: "Arial, sans-serif",
        }}
        width={width ?? rect.x * 2 + rect.w}
        height={height ?? rect.y * 2 + rect.h}
      >
        {/* items[0] paints first and sits at the bottom — see the schema. */}
        {doc.items.map((item) =>
          renderItem(item, rect, item.id === selectedId),
        )}
      </svg>
    );
  },
);

AnnotationLayer.displayName = "AnnotationLayer";
